import { Link } from "@inertiajs/react"
import { Check, Plus, Send, User, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import StarRating from "@/components/tuw/hire/StarRating"
import TierBadge from "@/components/tuw/hire/TierBadge"
import { canInvite } from "@/components/tuw/viewingRoom/Invitation"
import { VR, fill } from "@/lib/viewingRoom/wording"

// Side-by-side compare of ≤3 freelancers — a pure client-side view over the
// loaded result rows (net-new UI, no new API, per the build plan). The best cell
// in each comparable row is marked; shared niches are highlighted.
function replyMinutes(reply) {
  if (!reply) return Infinity
  const m = /([\d.]+)/.exec(reply)
  if (!m) return Infinity
  const n = parseFloat(m[1])
  if (/day/i.test(reply)) return n * 1440
  if (/hr/i.test(reply)) return n * 60
  return n
}

// Viewing Room: `guest` keeps every row, with the guest compare and invitation
// labels; a guest's profile links load the whole document (the profile's own
// head tags).

export default function CompareModal({ open, onOpenChange, freelancers, onRemove, onInvite, guest = false }) {
  const sel = freelancers ?? []
  const n = sel.length
  const ProfileLink = guest ? "a" : Link
  if (n === 0) return null

  const shared = sel.reduce((acc, f, i) => {
    const keys = new Set((f.niches ?? []).map((x) => x.key))
    return i === 0 ? keys : new Set([...acc].filter((k) => keys.has(k)))
  }, new Set())

  const ROWS = [
    {
      label: "Rating",
      better: "max",
      val: (f) => f.rating ?? -1,
      cell: (f) =>
        f.rating != null ? <StarRating rating={f.rating} count={f.reviews} /> : <span className="text-[13px] text-ink-3">Not yet rated</span>,
    },
    {
      label: "Reliability",
      better: "max",
      val: (f) => f.reliability ?? -1,
      cell: (f) => <div className="font-display text-[15px] font-bold">{f.reliability != null ? `${f.reliability}%` : "—"}</div>,
    },
    {
      label: "Avg. reply",
      better: "min",
      val: (f) => replyMinutes(f.replyTime),
      cell: (f) => <div className="text-[14px] font-semibold text-muted-foreground">{f.replyTime || "—"}</div>,
    },
    {
      label: "Availability",
      better: null,
      val: () => null,
      cell: (f) => <div className="text-[14px] font-semibold text-muted-foreground">{f.availability?.isNow ? "Open now" : f.availability?.label || "—"}</div>,
    },
    {
      label: "Current load",
      better: "min",
      val: (f) => f.activeOrders ?? Infinity,
      cell: (f) => <div className="text-[14px] font-semibold text-muted-foreground">{f.activeOrders === 0 ? "No active orders" : `${f.activeOrders} active`}</div>,
    },
    {
      label: "Languages",
      better: null,
      val: () => null,
      cell: (f) => <div className="text-[14px] font-semibold text-muted-foreground">{(f.languages ?? []).join(", ") || "—"}</div>,
    },
  ]

  const bestIndex = (row) => {
    if (!row.better) return -1
    const vals = sel.map(row.val)
    if (new Set(vals).size <= 1) return -1
    const target = row.better === "max" ? Math.max(...vals) : Math.min(...vals)
    return vals.indexOf(target)
  }

  // Columns keep a readable width; on a phone the table scrolls sideways.
  const cols = `120px repeat(${n}, minmax(170px, 1fr))`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="w-[min(900px,94vw)] max-w-[min(900px,94vw)] gap-0 overflow-hidden p-0 sm:max-w-[min(900px,94vw)]"
      >
        <div className="flex items-center gap-[12px] border-b border-border bg-card px-[22px] py-[18px]">
          <div>
            <div className="font-display text-[17px] font-bold tracking-[-0.3px]">{guest ? VR.compare.title : "Compare freelancers"}</div>
            <div className="mt-[2px] text-[12.5px] text-ink-3">
              {guest ? fill(VR.compare.selected, { count: n }) : `${n} selected · best in each row is marked`}
              {shared.size > 0 && ` · ${shared.size} shared niche${shared.size > 1 ? "s" : ""}`}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label={guest ? VR.compare.close : "Close"}
            className="ml-auto flex size-[36px] items-center justify-center rounded-[10px] border border-border bg-card text-muted-foreground hover:bg-secondary"
          >
            <X className="size-[17px]" strokeWidth={2} />
          </button>
        </div>

        <div className="max-h-[calc(90vh-84px)] overflow-auto px-[22px] py-[14px]">
          {/* header cards */}
          <div className="grid gap-[14px]" style={{ gridTemplateColumns: cols }}>
            <div />
            {sel.map((f) => (
              <div key={f.id} className="relative flex flex-col items-start gap-[9px] rounded-[15px] border border-border bg-card p-[16px]">
                <button
                  type="button"
                  onClick={() => onRemove?.(f)}
                  aria-label={guest ? fill(VR.compare.remove, { pen_name: f.penName }) : "Remove"}
                  className="absolute top-[10px] right-[10px] flex size-[24px] items-center justify-center rounded-[7px] bg-secondary text-ink-3 hover:bg-line-2 hover:text-foreground"
                >
                  <X className="size-[13px]" strokeWidth={2.4} />
                </button>
                <div>
                  <div className="font-display text-[15px] leading-tight font-bold tracking-[-0.2px]">{f.penName}</div>
                  <div className="mt-[2px] text-[11.5px] text-ink-3">{f.role}</div>
                </div>
                <TierBadge tier={f.tier} />
                <div className="mt-[5px] flex w-full flex-col gap-[7px]">
                  <Button size="sm" className="w-full justify-center" disabled={guest && !canInvite(f)} onClick={() => onInvite?.(f)}>
                    <Send className="size-[14px]" strokeWidth={2} />
                    {guest ? VR.action.invite : "Invite"}
                  </Button>
                  <Button asChild size="sm" variant="outline" className="w-full justify-center">
                    <ProfileLink href={`/freelancers/${f.id}`}>
                      <User className="size-[14px]" strokeWidth={2} />
                      View profile
                    </ProfileLink>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* metric rows */}
          {ROWS.map((row) => {
            const best = bestIndex(row)
            return (
              <div key={row.label} className="grid items-center gap-[14px] border-t border-border py-[15px]" style={{ gridTemplateColumns: cols }}>
                <div className="text-[10.5px] font-bold tracking-[0.06em] text-ink-3 uppercase">{row.label}</div>
                {sel.map((f, i) => (
                  <div key={f.id} className="flex min-w-0 flex-col gap-[3px]">
                    {row.cell(f)}
                    {best === i && (
                      <span className="inline-flex w-fit items-center gap-[3px] rounded-full border border-line-2 bg-secondary px-[5px] py-[2px] pr-[7px] text-[9px] font-bold tracking-[0.04em] text-foreground uppercase">
                        <Check className="size-[10px]" strokeWidth={3} />
                        Best
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )
          })}

          {/* niches row */}
          <div className="grid items-start gap-[14px] border-t border-border py-[15px]" style={{ gridTemplateColumns: cols }}>
            <div className="text-[10.5px] font-bold tracking-[0.06em] text-ink-3 uppercase">
              Niches
              {shared.size > 0 && (
                <span className="block text-[10px] font-semibold tracking-normal text-muted-foreground normal-case">{shared.size} shared</span>
              )}
            </div>
            {sel.map((f) => (
              <div key={f.id} className="flex flex-wrap gap-[5px]">
                {(f.niches ?? []).map((nch) => (
                  <span
                    key={nch.key}
                    className={cn(
                      "rounded-[7px] px-[9px] py-[3px] text-[11px] font-semibold",
                      shared.has(nch.key) ? "bg-foreground text-background" : "bg-secondary text-muted-foreground"
                    )}
                  >
                    {nch.label}
                  </span>
                ))}
              </div>
            ))}
          </div>

          {n < 3 && (
            <div className="mt-[16px] flex items-center gap-[8px] rounded-[12px] border border-dashed border-line-2 bg-accent px-[16px] py-[13px] text-[12.5px] text-ink-3">
              <Plus className="size-[15px]" strokeWidth={2} />
              Compare up to 3 — close this and tick one more in the roster.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
